/**
 * Hooks for managing document chat/timeline
 */

import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { erp } from "@/lib/erp";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";
import type { DocChatItem, DocChatResponse, DocSidebarData } from "@/lib/erp/docs";

/**
 * Fetch paginated chat timeline for a document using infinite query
 * 
 * Returns items in chronological order (oldest → newest) for display.
 * Ensures global ordering across all pages and deduplicates items.
 */
export function useDocChat(doctype: string, name: string | undefined) {
  return useInfiniteQuery({
    queryKey: ["doc-chat", doctype, name],
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      if (!name) throw new Error("Document name is required");
      return erp.docs.getDocChat(doctype, name, {
        limit: 30,
        before: pageParam, // Passes cursor to backend for pagination
      });
    },
    enabled: !!name,
    getNextPageParam: (lastPage: DocChatResponse) => {
      // Returns next_before cursor for pagination (snake_case from backend)
      return lastPage.has_more ? lastPage.next_before ?? undefined : undefined;
    },
    initialPageParam: undefined as string | undefined,
    select: (data) => {
      // Merge all pages, dedupe, and sort globally
      const pages = data?.pages ?? [];
      
      // Merge all items from all pages
      const merged = pages.flatMap((page) => page.items ?? []);
      
      // Dedupe by name (defensive - prevents duplicates if backend has issues)
      // This ensures no duplicate items appear even if pagination overlaps
      const byName = new Map<string, DocChatItem>();
      for (const item of merged) {
        if (!byName.has(item.name)) {
          byName.set(item.name, item);
        }
      }
      
      // Sort globally by creation time (oldest → newest)
      // This ensures: top = oldest, bottom = newest (Google Chat style)
      // Backend returns creation as ISO 8601 string, so new Date() is safe
      const items = Array.from(byName.values()).sort(
        (a, b) => new Date(a.creation).getTime() - new Date(b.creation).getTime()
      );
      
      return {
        items,
        hasMore: pages[pages.length - 1]?.has_more ?? false, // Remap snake_case to camelCase
      };
    },
  });
}

/**
 * Add a new comment to a document
 * 
 * Updates sidebar comment count optimistically and refetches chat to get latest data.
 */
export function useAddDocComment(doctype: string, name: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: { content: string; mentions?: string[]; reply_to?: string }) => {
      return erp.docs.addDocComment({
        doctype,
        name,
        content: params.content,
        mentions: params.mentions,
        reply_to: params.reply_to,
      });
    },
    onSuccess: () => {
      // 1) Update sidebar comment count optimistically
      queryClient.setQueryData<DocSidebarData>(
        ["doc-sidebar", doctype, name],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            comment_count: (old.comment_count || 0) + 1,
          };
        }
      );

      // 2) Invalidate active infinite query -> React Query refetches automatically
      queryClient.invalidateQueries({
        queryKey: ["doc-chat", doctype, name],
        refetchType: "active", // Force refetch of active queries
      });

      // 3) Sidebar can stay invalidate-only (count is already optimistic)
      queryClient.invalidateQueries({
        queryKey: ["doc-sidebar", doctype, name],
      });
    },
    onError: (error) => {
      console.error("Failed to add comment", error);
      toast.error("Failed to send message");
    },
  });
}

/**
 * Delete a comment (own messages, within 15-minute window)
 *
 * Optimistically removes the item from cache for instant feedback.
 * Rolls back on error.
 */
export function useDeleteComment(doctype: string, name: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commentName: string) => {
      return erp.docs.deleteDocComment({ comment_name: commentName });
    },
    onMutate: async (commentName) => {
      await queryClient.cancelQueries({ queryKey: ["doc-chat", doctype, name] });
      const previous = queryClient.getQueryData(["doc-chat", doctype, name]);
      queryClient.setQueryData(["doc-chat", doctype, name], (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.filter((item: any) => item.name !== commentName),
          })),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["doc-chat", doctype, name], context.previous);
      }
      toast.error("Failed to delete message");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["doc-chat", doctype, name] });
      queryClient.setQueryData<DocSidebarData>(
        ["doc-sidebar", doctype, name],
        (old) => {
          if (!old) return old;
          return { ...old, comment_count: Math.max(0, (old.comment_count || 0) - 1) };
        }
      );
    },
  });
}

// ── Optimistic reaction toggle helper ────────────────────────────────────────

function toggleReactionOptimistic(
  item: DocChatItem,
  emoji: string,
  currentUser: string
): DocChatItem {
  const reactions = [...(item.reactions ?? [])];
  const idx = reactions.findIndex((r) => r.emoji === emoji);

  if (idx >= 0) {
    const reaction = reactions[idx];
    if (reaction.has_reacted) {
      // Remove current user's reaction
      const newUsers = reaction.users.filter((u) => u !== currentUser);
      if (newUsers.length === 0) {
        reactions.splice(idx, 1);
      } else {
        reactions[idx] = { ...reaction, count: newUsers.length, users: newUsers, has_reacted: false };
      }
    } else {
      // Add current user's reaction
      reactions[idx] = {
        ...reaction,
        count: reaction.count + 1,
        users: [...reaction.users, currentUser],
        has_reacted: true,
      };
    }
  } else {
    // New emoji reaction
    reactions.push({ emoji, count: 1, users: [currentUser], has_reacted: true });
  }

  return { ...item, reactions };
}

/**
 * Toggle a reaction on a comment
 *
 * Optimistically updates the reaction in the infinite query cache.
 * Rolls back on error.
 */
export function useToggleReaction(doctype: string, name: string) {
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.email || currentUser?.name || "";

  return useMutation({
    mutationFn: async (params: { commentName: string; emoji: string }) =>
      erp.docs.toggleCommentReaction({
        comment_name: params.commentName,
        emoji: params.emoji,
      }),
    onMutate: async ({ commentName, emoji }) => {
      await queryClient.cancelQueries({ queryKey: ["doc-chat", doctype, name] });
      const previous = queryClient.getQueryData(["doc-chat", doctype, name]);

      queryClient.setQueryData(["doc-chat", doctype, name], (old: any) => {
        if (!old?.pages) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.map((item: any) => {
              if (item.name !== commentName) return item;
              return toggleReactionOptimistic(item, emoji, userId);
            }),
          })),
        };
      });
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["doc-chat", doctype, name], context.previous);
      }
      toast.error("Failed to update reaction");
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["doc-chat", doctype, name] });
    },
  });
}

